import * as React from 'react';
import SpeedOutlined from '@mui/icons-material/SpeedOutlined';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import FolderCopyOutlined from '@mui/icons-material/FolderCopyOutlined';
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined';
import BoltOutlined from '@mui/icons-material/BoltOutlined';
import GridViewOutlined from '@mui/icons-material/GridViewOutlined';
import WidgetsOutlined from '@mui/icons-material/WidgetsOutlined';
import CloudOutlined from '@mui/icons-material/CloudOutlined';
import FormatListNumberedOutlined from '@mui/icons-material/FormatListNumberedOutlined';
import SellOutlined from '@mui/icons-material/SellOutlined';
import WebOutlined from '@mui/icons-material/WebOutlined';
import ContactPageOutlined from '@mui/icons-material/ContactPageOutlined';
import LinkOutlined from '@mui/icons-material/LinkOutlined';
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import NotificationsNoneOutlined from '@mui/icons-material/NotificationsNoneOutlined';
import RssFeedOutlined from '@mui/icons-material/RssFeedOutlined';
import StorageOutlined from '@mui/icons-material/StorageOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import ListAltOutlined from '@mui/icons-material/ListAltOutlined';
import BusinessOutlined from '@mui/icons-material/BusinessOutlined';
import LabelOutlined from '@mui/icons-material/LabelOutlined';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import SignpostOutlined from '@mui/icons-material/SignpostOutlined';
import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import PieChartOutlined from '@mui/icons-material/PieChartOutlined';
import LanguageOutlined from '@mui/icons-material/LanguageOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import TuneOutlined from '@mui/icons-material/TuneOutlined';
import AppsOutlined from '@mui/icons-material/AppsOutlined';
import WorkspacesOutlined from '@mui/icons-material/WorkspacesOutlined';
import PublicOutlined from '@mui/icons-material/PublicOutlined';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import ViewQuiltOutlined from '@mui/icons-material/ViewQuiltOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import CodeOutlined from '@mui/icons-material/CodeOutlined';
import HubOutlined from '@mui/icons-material/HubOutlined';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import PinDropOutlined from '@mui/icons-material/PinDropOutlined';
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined';
import ToggleOnOutlined from '@mui/icons-material/ToggleOnOutlined';
import GroupOutlined from '@mui/icons-material/GroupOutlined';
import BuildOutlined from '@mui/icons-material/BuildOutlined';
import AddLinkOutlined from '@mui/icons-material/AddLinkOutlined';

export interface NavLeaf {
  id: string;
  label: string;
  /** In-app route (react-router). */
  to?: string;
  /** External link, opened in a new tab. */
  href?: string;
  icon: React.ReactNode;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: React.ReactNode;
  children: NavLeaf[];
}

export type NavItem = NavLeaf | NavGroup;

export const isNavGroup = (item: NavItem): item is NavGroup => 'children' in item;

/** Sidebar navigation. Data only — rendering lives in containers/DefaultLayout/AppSidebarNav. */
export const navItems: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', to: '/dashboard', icon: <SpeedOutlined /> },
  {
    id: 'campaigns',
    label: 'Campaigns',
    icon: <CampaignOutlined />,
    children: [
      { id: 'landingPages', label: 'Landing Pages', to: '/campaigns/landingpages', icon: <DescriptionOutlined /> },
      {
        id: 'landingPageGroups',
        label: 'Landing Page Groups',
        to: '/campaigns/landingpagegroups',
        icon: <FolderCopyOutlined />,
      },
      { id: 'offers', label: 'Offers', to: '/campaigns/offertable', icon: <LocalOfferOutlined /> },
      { id: 'offerActions', label: 'Offer Actions', to: '/campaigns/offeractions', icon: <BoltOutlined /> },
      { id: 'offerWallAds', label: 'Offer Wall Ads', to: '/campaigns/offerwallads', icon: <GridViewOutlined /> },
      { id: 'offerBlocks', label: 'Offer Blocks', to: '/campaigns/offerblocks', icon: <WidgetsOutlined /> },
      {
        id: 'dynamicOfferSections',
        label: 'Dynamic Sections',
        to: '/campaigns/dynamic-offer-sections',
        icon: <CloudOutlined />,
      },
      { id: 'sequences', label: 'Sequences', to: '/campaigns/sequences', icon: <FormatListNumberedOutlined /> },
      { id: 'tags', label: 'Tags', to: '/campaigns/tags', icon: <SellOutlined /> },
      { id: 'sites', label: 'Sites', to: '/campaigns/sites', icon: <WebOutlined /> },
    ],
  },
  { id: 'advertisers', label: 'Advertisers', to: '/advertisers', icon: <ContactPageOutlined /> },
  { id: 'marketingPartners', label: 'Marketing Partners', to: '/marketingpartners', icon: <LinkOutlined /> },
  { id: 'affiliates', label: 'Affiliates', to: '/affiliates', icon: <HandshakeOutlined /> },
  {
    id: 'publisher-pre-pings',
    label: 'Publisher Pre-Pings',
    to: '/publisher-pre-pings',
    icon: <NotificationsNoneOutlined />,
  },
  { id: 'pubxml-feeds', label: 'PubXML Feeds', to: '/pubxml-feeds', icon: <RssFeedOutlined /> },
  { id: 'source-feeds', label: 'Source Feeds', to: '/source-feeds', icon: <StorageOutlined /> },
  {
    id: 'generic-jobs-group',
    label: 'Generic Jobs',
    icon: <WorkOutlineOutlined />,
    children: [
      { id: 'generic-jobs', label: 'Jobs', to: '/generic-jobs', icon: <ListAltOutlined /> },
      { id: 'generic-jobs-companies', label: 'Companies', to: '/generic-jobs/companies', icon: <BusinessOutlined /> },
      {
        id: 'generic-jobs-categories',
        label: 'Job Categories',
        to: '/generic-jobs/job-categories',
        icon: <LabelOutlined />,
      },
    ],
  },
  {
    id: 'virtual-source-feeds',
    label: 'Virtual Source Feeds',
    to: '/virtual-source-feeds',
    icon: <AccountTreeOutlined />,
  },
  { id: 'field-mappings', label: 'Field Mappings', to: '/field-mappings', icon: <SignpostOutlined /> },
  {
    id: 'advertiser-pre-pings',
    label: 'Advertiser Pre-Ping',
    to: '/advertiser-pre-pings',
    icon: <NotificationsActiveOutlined />,
  },
  { id: 'revshare-percentage', label: 'Revshare Percentage', to: '/revshare-percentage', icon: <PieChartOutlined /> },
  { id: 'domains', label: 'Domains', to: '/domains', icon: <LanguageOutlined /> },
  {
    id: 'custom-properties',
    label: 'Custom Properties',
    icon: <SettingsOutlined />,
    children: [
      { id: 'properties', label: 'Properties', to: '/custom-properties/properties', icon: <TuneOutlined /> },
      { id: 'property-sets', label: 'Property Sets', to: '/custom-properties/property-sets', icon: <AppsOutlined /> },
      {
        id: 'entity-groups',
        label: 'Entity Groups',
        to: '/custom-properties/entity-groups',
        icon: <WorkspacesOutlined />,
      },
      {
        id: 'global-properties',
        label: 'Global Properties',
        to: '/custom-properties/global-properties',
        icon: <PublicOutlined />,
      },
    ],
  },
  {
    id: 'content',
    label: 'Content',
    icon: <ArticleOutlined />,
    children: [
      { id: 'layouts', label: 'Layouts', to: '/content/layouts', icon: <ViewQuiltOutlined /> },
      { id: 'containers', label: 'Containers', to: '/content/containers', icon: <Inventory2Outlined /> },
      { id: 'templateEditor', label: 'Template Editor', to: '/content/template-editor', icon: <CodeOutlined /> },
      { id: 'handlers', label: 'Handlers', to: '/content/handlers', icon: <HubOutlined /> },
      {
        id: 'userAttributes',
        label: 'User Attributes',
        to: '/content/userAttributes',
        icon: <PersonOutlineOutlined />,
      },
      { id: 'zipCodes', label: 'Zip Codes', to: '/content/zipCodes', icon: <PinDropOutlined /> },
    ],
  },
  {
    id: 'tools',
    label: 'Tools',
    icon: <BuildOutlined />,
    children: [
      { id: 'testLinkBuilder', label: 'Test Link Builder', to: '/tools/test-link-builder', icon: <AddLinkOutlined /> },
    ],
  },
  { id: 'mediaLibrary', label: 'Media Library', to: '/media-library', icon: <ImageOutlined /> },
  { id: 'staffPermissions', label: 'Staff Permissions', to: '/permissions', icon: <AdminPanelSettingsOutlined /> },
  { id: 'features', label: 'Features', to: '/features', icon: <ToggleOnOutlined /> },
  { id: 'users', label: 'Users', to: '/users', icon: <GroupOutlined /> },
];
